import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    ArrowLeft,
    Save,
    Upload,
    FileText,
    Loader2,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SimpleEditor } from '@/components/editor';
import { Label } from '@/components/ui/label';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { createInsight } from '@/services/insight';
import { uploadFile } from '@/services/upload';
import { encodeS3Url } from '@/components/encodeS3Url';

const formSchema = z.object({
    title: z.string().min(5, { message: "Title must be at least 5 characters" }),
    description: z.string().min(10, { message: "Description must be at least 10 characters" }),
    contentHTML: z.string().min(50, { message: "Content must be at least 50 characters" }),
    contentJSON: z.any({ required_error: "Editor content is required" }),
    category: z.string().min(1, { message: "Category is required" }),
    status: z.enum(['draft', 'published']),
    featuredImage: z.string().optional(),
    pdfFile: z.string().optional(),
    fileSize: z.string().optional(),
});

const MarketInsightCreate = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [isPdfUploading, setIsPdfUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [pdfName, setPdfName] = useState<string | null>(null);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: '',
            description: '',
            contentHTML: '',
            contentJSON: {},
            category: '',
            status: 'draft',
            featuredImage: '',
            pdfFile: '',
            fileSize: '0 MB',
        },
    });

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsImageUploading(true);

            try {
                const imageUrl = await uploadFile(file);
                form.setValue('featuredImage', imageUrl);
                setImagePreview(imageUrl); // Set preview with the uploaded URL
                toast({
                    title: "Success",
                    description: "Image uploaded successfully",
                });
            } catch (error) {
                console.error("Error uploading image:", error);
                setImagePreview(null);
                form.setValue('featuredImage', '');
                toast({
                    title: "Error",
                    description: "Failed to upload image",
                    variant: "destructive"
                });
            } finally {
                setIsImageUploading(false);
            }
        }
    };

    const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsPdfUploading(true);

            try {
                const fileUrl = await uploadFile(file);

                setPdfName(file.name);
                const fileSize = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
                form.setValue('fileSize', fileSize);
                form.setValue('pdfFile', fileUrl);

                toast({
                    title: "Success",
                    description: "PDF uploaded successfully",
                });
            } catch (error) {
                console.error("Error uploading PDF:", error);
                setPdfName(null);
                form.setValue('fileSize', '0 MB');
                form.setValue('pdfFile', '');
                toast({
                    title: "Error",
                    description: "Failed to upload PDF",
                    variant: "destructive"
                });
            } finally {
                setIsPdfUploading(false);
            }
        }
    };

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsLoading(true);
        try {
            await createInsight(values);
            toast({
                title: "Success",
                description: "Market insight created successfully.",
            });
            navigate('/admin/market-insights');
        } catch (error: any) {
            console.error("Error creating insight:", error);
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to create market insight.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="mb-8 flex items-center gap-4">
                        <Link to="/admin/market-insights">
                            <Button variant="outline" size="icon">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        </Link>
                        <div>
                            <h1 className="text-3xl font-bold">Add New Market Insight</h1>
                            <p className="text-muted-foreground">
                                Create a new market report or insight
                            </p>
                        </div>
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-8">
                                    {/* Report Details */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Report Details</CardTitle>
                                            <CardDescription>
                                                Enter the basic details of your market insight report
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            <FormField
                                                control={form.control}
                                                name="title"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Report Title</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="e.g. Dubai Real Estate Market Report Q2 2023" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="description"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Short Description</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Brief description of the report (1-2 sentences)"
                                                                className="min-h-[100px]"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <FormField
                                                    control={form.control}
                                                    name="category"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Category</FormLabel>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select category" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    <SelectItem value="market-analysis">Market Analysis</SelectItem>
                                                                    <SelectItem value="investment">Investment Opportunities</SelectItem>
                                                                    <SelectItem value="trends">Market Trends</SelectItem>
                                                                    <SelectItem value="forecast">Market Forecast</SelectItem>
                                                                    <SelectItem value="rental">Rental Market</SelectItem>
                                                                    <SelectItem value="commercial">Commercial Real Estate</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <FormField
                                                    control={form.control}
                                                    name="status"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Status</FormLabel>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select status" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    <SelectItem value="draft">Draft</SelectItem>
                                                                    <SelectItem value="published">Published</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Content */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Report Content</CardTitle>
                                            <CardDescription>
                                                Write the full content of your market insight report
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="contentHTML"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Content</FormLabel>
                                                        <FormControl>
                                                            <SimpleEditor
                                                                content={field.value}
                                                                contentJSON={form.getValues('contentJSON')}
                                                                onChange={(html, json) => {
                                                                    field.onChange(html);
                                                                    form.setValue('contentJSON', json);
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Use the editor above to format your market insight report content.
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </div>

                                <div className="space-y-8">
                                    {/* Publishing Actions */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Publishing</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <Button type="submit" className="w-full" disabled={isLoading}>
                                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                {form.watch('status') === 'published' ? 'Publish Report' : 'Save Draft'}
                                            </Button>
                                        </CardContent>
                                    </Card>

                                    {/* Featured Image */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Featured Image</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                                <div className="flex flex-col items-center">
                                                    {isImageUploading ? (
                                                        <div className="flex flex-col items-center justify-center py-4">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                                                            <p className="text-sm text-muted-foreground">Uploading image...</p>
                                                        </div>
                                                    ) : imagePreview ? (
                                                        <div className="relative w-full mb-4">
                                                            <img src={encodeS3Url(imagePreview)} alt="Preview" className="w-full h-48 object-cover rounded-md" />
                                                            <Button
                                                                type="button"
                                                                variant="destructive"
                                                                size="icon"
                                                                className="absolute top-2 right-2"
                                                                onClick={() => {
                                                                    setImagePreview(null);
                                                                    form.setValue('featuredImage', '');
                                                                }}
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <Upload className="h-8 w-8 text-muted-foreground mb-4" />
                                                            <p className="mb-2 text-sm font-semibold">
                                                                Drag & drop or click to upload
                                                            </p>
                                                            <p className="mb-4 text-xs text-muted-foreground">
                                                                SVG, PNG, JPG or WebP (Max 2MB)
                                                            </p>
                                                        </>
                                                    )}
                                                    <Input
                                                        id="featuredImage"
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={handleImageUpload}
                                                    />
                                                    {!imagePreview && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => document.getElementById("featuredImage")?.click()}
                                                        >
                                                            Browse Files
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* PDF Report */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>PDF Report (Optional)</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                                <div className="flex flex-col items-center">
                                                    <FileText className="h-8 w-8 text-muted-foreground mb-4" />
                                                    {isPdfUploading ? (
                                                        <div className="flex flex-col items-center justify-center py-4">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                                                            <p className="text-sm text-muted-foreground">Uploading PDF...</p>
                                                        </div>
                                                    ) : pdfName ? (
                                                        <div className="mb-4">
                                                            <p className="text-sm font-medium text-green-600 truncate max-w-[200px]">
                                                                {pdfName}
                                                            </p>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-destructive mt-1"
                                                                onClick={() => {
                                                                    setPdfName(null);
                                                                    form.setValue('pdfFile', '');
                                                                    form.setValue('fileSize', '0 MB');
                                                                }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <p className="mb-2 text-sm font-semibold">
                                                                Drag & drop your PDF report here
                                                            </p>
                                                            <p className="mb-4 text-xs text-muted-foreground">
                                                                PDF files only (Max 10MB)
                                                            </p>
                                                        </>
                                                    )}
                                                    <Input
                                                        id="pdfFile"
                                                        type="file"
                                                        accept=".pdf"
                                                        className="hidden"
                                                        onChange={handlePdfUpload}
                                                    />
                                                    {!pdfName && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => document.getElementById("pdfFile")?.click()}
                                                        >
                                                            Browse Files
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default MarketInsightCreate;
